<template>
  <button type="button" class="trial-row" @click="$emit('open', customer)">
    <span class="identity">
      <strong>{{ customer.name || "이름 없음" }}</strong>
      <small>{{ prettyPhone(customer.phone_number) }}</small>
      <small v-if="visitSummaryText" class="visit-summary">{{
        visitSummaryText
      }}</small>
    </span>
    <span class="schedule">
      <small>{{ visitKindLabel(customer.visit_kind) }} 일정</small>
      <strong>{{ visitText }}</strong>
      <em v-if="customer.visit?.class_title">{{
        customer.visit.class_title
      }}</em>
    </span>
    <span class="badges">
      <span class="badge" :class="`visit-${visitStatus}`">{{
        visitLabel
      }}</span>
      <span class="badge consultation">{{ consultationLabel }}</span>
      <span v-if="customer.registration?.registered" class="badge registered"
        >등록</span
      >
      <span
        v-else-if="customer.registration?.existing_member"
        class="badge existing-member"
        >기존 회원</span
      >
    </span>
    <span class="owner">
      <small>담당</small>
      <strong>{{ customer.profile?.assigned_coach_name || "미지정" }}</strong>
    </span>
    <span class="next-action">
      <small>다음 행동</small>
      <strong>{{ followUpText }}</strong>
    </span>
    <span class="arrow" aria-hidden="true">›</span>
  </button>
</template>

<script>
import {
  CONSULTATION_STATUSES,
  optionLabel,
  prettyPhone,
  shortDateTime,
  trialVisitSummaryText,
  visitKindLabel,
  visitStatusLabel,
} from "@/lib/trialCRM";

export default {
  name: "TrialCustomerRow",
  props: { customer: { type: Object, required: true } },
  emits: ["open"],
  computed: {
    visitStatus() {
      return this.customer.visit?.status || "unknown";
    },
    visitLabel() {
      return visitStatusLabel(this.visitStatus);
    },
    // 한 줄은 에피소드 하나다. 체험인지 드랍인인지 목록에서 바로 읽히게 방문 요약을 쓴다
    visitSummaryText() {
      return trialVisitSummaryText(this.customer.visit_summary);
    },
    consultationLabel() {
      return optionLabel(
        CONSULTATION_STATUSES,
        this.customer.profile?.consultation_status,
        "상담 전"
      );
    },
    visitText() {
      const visit = this.customer.visit || {};
      if (!visit.class_date) return "일정 미정";
      return [visit.class_date, visit.start_time].filter(Boolean).join(" ");
    },
    followUpText() {
      return this.customer.profile?.next_follow_up_at
        ? shortDateTime(this.customer.profile.next_follow_up_at)
        : "—";
    },
  },
  methods: { prettyPhone, visitKindLabel },
};
</script>

<style scoped>
.trial-row {
  width: 100%;
  min-height: 86px;
  display: grid;
  grid-template-columns:
    minmax(160px, 1.25fr) minmax(150px, 1.15fr) minmax(190px, 1.5fr)
    minmax(100px, 0.7fr) minmax(125px, 0.85fr) 20px;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid #2c3031;
  border-radius: 12px;
  background: #17191a;
  color: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease,
    transform 0.16s ease;
}
.trial-row:hover,
.trial-row:focus-visible {
  background: #1d2021;
  border-color: #536056;
  outline: none;
}
.trial-row:active {
  transform: translateY(1px);
}
.identity,
.schedule,
.owner,
.next-action {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}
.identity strong {
  font-size: 16px;
}
.visit-summary {
  color: #b2b9b4;
}
small,
em {
  overflow: hidden;
  color: #8f9692;
  font-size: 12px;
  font-style: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.schedule strong,
.owner strong,
.next-action strong {
  overflow: hidden;
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.badge {
  padding: 4px 8px;
  border: 1px solid #414748;
  border-radius: 999px;
  color: #cbd0cd;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.visit-booked {
  border-color: #5075a8;
  color: #8cb8f1;
}
.visit-attended,
.registered {
  border-color: #4a7c3a;
  color: #78ff47;
  background: rgba(120, 255, 71, 0.07);
}
.existing-member {
  border-color: #66548a;
  color: #c7aef7;
}
.visit-cancelled {
  border-color: #7a3f43;
  color: #ff8b91;
}
.consultation {
  color: #e1c47b;
}
.arrow {
  color: #767d79;
  font-size: 24px;
}

@media (max-width: 980px) {
  .trial-row {
    grid-template-columns: 1.2fr 1fr auto 18px;
  }
  .owner,
  .next-action {
    display: none;
  }
}
@media (max-width: 640px) {
  .trial-row {
    grid-template-columns: 1fr auto;
    gap: 10px;
    padding: 14px;
  }
  .schedule {
    grid-column: 1;
  }
  .badges {
    grid-column: 1 / -1;
  }
  .arrow {
    grid-column: 2;
    grid-row: 1 / 3;
  }
}
</style>
